import { useEffect, useRef, useCallback } from "react";
import { useSlicerFilterState } from "@lhd/components/SearchAreaBox/hooks/useSlicerFilterState";
import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";
import { FilterSelected } from "@lhd/components/SearchAreaBox/types";

const STATUS = { CHECKED: "CHECKED", UNCHECKED: "UNCHECKED" } as const;
const SPECIAL_NULL = "__NULL__";

const isKeyMatchingOption = (keys: any[], option: any) =>
  keys.includes(option.value) ||
  keys.includes(String(option.value)) ||
  keys.includes(Number(option.value)) ||
  keys.includes(option.label) ||
  keys.includes(String(option.label));

// Every grid option is selected and the special-empty option is not excluded
const isAllOptionsChecked = (
  keys: any[],
  filterOptions: any[] | undefined,
  filterSpecialEmpty: boolean | undefined,
) =>
  Array.isArray(filterOptions) &&
  filterOptions.length > 0 &&
  filterOptions.every((o: any) => isKeyMatchingOption(keys, o)) &&
  filterSpecialEmpty !== false;

const buildInitialFilterSelected = (
  filterOptions: any[],
  keys: any[],
  isCheckAll: boolean,
): FilterSelected[] =>
  filterOptions.map((option: any) => {
    const isOptionChecked =
      isCheckAll ||
      keys.includes(option.value) ||
      keys.includes(String(option.value)) ||
      keys.includes(Number(option.value)) ||
      keys.includes(option.label) ||
      keys.includes(String(option.label)) ||
      keys.includes(option.text) ||
      keys.includes(String(option.text));

    return {
      value: option.value,
      label: option.label,
      status: isOptionChecked ? STATUS.CHECKED : STATUS.UNCHECKED,
    };
  });

const initFilterSelected = (
  filterKeys: string[],
  currentFilter: Record<string, any>,
  setFilterState: React.Dispatch<React.SetStateAction<FilterSelected[]>>,
) => {
  filterKeys.forEach((key) => {
    const currFilterObj = currentFilter[key];
    const filterOptions = currFilterObj?.filterOptions;
    const filterVal = currFilterObj?.filter;
    const keys = Array.isArray(filterVal)
      ? filterVal
      : typeof filterVal === "object" && filterVal !== null
      ? Object.keys(filterVal).filter((k) => filterVal[k] === true)
      : [];

    const isCheckAll = Boolean(
      currFilterObj?.isCheckAll ||
        filterVal === undefined ||
        filterVal === null ||
        isAllOptionsChecked(
          keys,
          filterOptions,
          currFilterObj?.filter_special_empty,
        ),
    );

    if (Array.isArray(filterOptions) && filterOptions.length > 0) {
      setFilterState(
        buildInitialFilterSelected(filterOptions, keys, isCheckAll),
      );
    }
  });
};

interface UseSearchAreaBoxFilterSyncProps {
  filterInfo: FilterInfo;
  setFilterInfo: React.Dispatch<React.SetStateAction<FilterInfo>>;
  statusKeys?: string[];
  categoryKeys?: string[];
  gridApi?: any;
  selectedTabs?: string[];
  controlledStatusFilterSelected?: FilterSelected[];
  setControlledStatusFilterSelected?: React.Dispatch<
    React.SetStateAction<FilterSelected[]>
  >;
  controlledCategoryFilterSelected?: FilterSelected[];
  setControlledCategoryFilterSelected?: React.Dispatch<
    React.SetStateAction<FilterSelected[]>
  >;
}

export const useSearchAreaBoxFilterSync = ({
  filterInfo,
  setFilterInfo,
  statusKeys = [],
  categoryKeys = [],
  gridApi,
  selectedTabs = [],
  controlledStatusFilterSelected,
  setControlledStatusFilterSelected,
  controlledCategoryFilterSelected,
  setControlledCategoryFilterSelected,
}: UseSearchAreaBoxFilterSyncProps) => {
  const [internalStatusFilterSelected, setInternalStatusFilterSelected] =
    useSlicerFilterState<FilterSelected[]>("status", []);
  const [internalCategoryFilterSelected, setInternalCategoryFilterSelected] =
    useSlicerFilterState<FilterSelected[]>("category", []);

  const statusFilterSelected =
    controlledStatusFilterSelected !== undefined
      ? controlledStatusFilterSelected
      : internalStatusFilterSelected;
  const setStatusFilterSelected =
    setControlledStatusFilterSelected !== undefined
      ? setControlledStatusFilterSelected
      : setInternalStatusFilterSelected;

  const categoryFilterSelected =
    controlledCategoryFilterSelected !== undefined
      ? controlledCategoryFilterSelected
      : internalCategoryFilterSelected;
  const setCategoryFilterSelected =
    setControlledCategoryFilterSelected !== undefined
      ? setControlledCategoryFilterSelected
      : setInternalCategoryFilterSelected;

  const previousFilterInfoRef = useRef<Record<string, any>>({});
  const previousStatusFilterSelectedRef = useRef<FilterSelected[]>([]);
  const previousCategoryFilterSelectedRef = useRef<FilterSelected[]>([]);
  const filterInfoRef = useRef(filterInfo);
  const shouldRefreshHeaderRef = useRef(false);
  const userActionSourceRef = useRef<"status" | "category" | null>(null);

  useEffect(() => {
    filterInfoRef.current = filterInfo;
  }, [filterInfo]);

  // Sync from filterInfo to statusFilterSelected/categoryFilterSelected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const prevFilter = previousFilterInfoRef.current;
    const currentFilter = filterInfo.filter || {};

    const syncFilterState = (
      currTargetFilter: any,
      prevTargetFilter: any,
      setFilterState: React.Dispatch<React.SetStateAction<FilterSelected[]>>,
      filterOptions: any[] | undefined,
      isUserActionSource: boolean,
    ) => {
      const isSpecialEmptyChanged =
        (prevTargetFilter?.filter_special_empty || false) !==
        (currTargetFilter?.filter_special_empty || false);

      const isFilterArrayChanged =
        Array.isArray(currTargetFilter?.filter) &&
        Array.isArray(prevTargetFilter?.filter)
          ? currTargetFilter.filter.length !== prevTargetFilter.filter.length ||
            currTargetFilter.filter.some(
              (v: any) => !prevTargetFilter.filter.includes(v),
            )
          : JSON.stringify(currTargetFilter?.filter) !==
            JSON.stringify(prevTargetFilter?.filter);

      if (prevTargetFilter && !currTargetFilter && !isUserActionSource) {
        setFilterState((prev) =>
          prev.map((item) => ({
            ...item,
            status: STATUS.CHECKED,
          })),
        );
        return;
      }

      if (isFilterArrayChanged || isSpecialEmptyChanged) {
        if (
          currTargetFilter?.filter ||
          currTargetFilter?.filter_special_empty
        ) {
          const keys = Array.isArray(currTargetFilter.filter)
            ? currTargetFilter.filter
            : typeof currTargetFilter.filter === "object" &&
              currTargetFilter.filter !== null
            ? Object.keys(currTargetFilter.filter).filter(
                (k) => currTargetFilter.filter[k] === true,
              )
            : Object.keys(currTargetFilter.filter || {});

          const isGridUncheckAll =
            keys.length === 0 && !currTargetFilter.filter_special_empty;

          const optionValues =
            filterOptions?.map((o: any) => String(o.value)) || [];

          const isTargetCheckAll = Boolean(
            currTargetFilter?.isCheckAll ||
              isAllOptionsChecked(
                keys,
                filterOptions,
                currTargetFilter?.filter_special_empty,
              ),
          );

          setFilterState((prev) => {
            const updated = prev.map((item) => {
              let gridValue = item.value;
              if (filterOptions) {
                const matchedOption = filterOptions.find(
                  (o: any) =>
                    String(o.value) === String(item.value) ||
                    String(o.label) === String(item.value) ||
                    String(o.label) === String(item.label) ||
                    String(o.value) === String(item.label),
                );
                if (matchedOption) {
                  gridValue = matchedOption.value;
                }
              }

              const isInCurrentFilter =
                isTargetCheckAll ||
                keys.includes(gridValue) ||
                keys.includes(String(gridValue)) ||
                keys.includes(Number(gridValue)) ||
                keys.includes(item.value) ||
                keys.includes(String(item.value)) ||
                keys.includes(Number(item.value)) ||
                keys.includes((item as any).label) ||
                keys.includes(String((item as any).label)) ||
                keys.includes((item as any).text) ||
                keys.includes(String((item as any).text)) ||
                (item.value === SPECIAL_NULL &&
                  (currTargetFilter.filter_special_empty || isTargetCheckAll));

              const isValidAGGridValue =
                filterOptions === undefined ||
                optionValues.includes(String(gridValue)) ||
                optionValues.includes(String(item.value)) ||
                item.value === SPECIAL_NULL;

              // Keep state on this side if option is not in Grid, UNLESS when Uncheck All
              if (!isValidAGGridValue && !isGridUncheckAll) {
                return item;
              }

              // Sync when present on both sides (or when Uncheck All)
              return {
                ...item,
                status: isInCurrentFilter ? STATUS.CHECKED : STATUS.UNCHECKED,
              };
            });

            return updated;
          });
        } else if (
          currTargetFilter &&
          !currTargetFilter.filter &&
          !currTargetFilter.filter_special_empty
        ) {
          setFilterState((prev) => {
            const updated = prev.map((item) => ({
              ...item,
              status: STATUS.CHECKED,
            }));
            return updated;
          });
        }
      }
    };

    const processCategoryOrStatus = (
      keys: string[],
      setFilterState: React.Dispatch<React.SetStateAction<FilterSelected[]>>,
      sourceType: "status" | "category",
    ) => {
      const changedKey = keys.find(
        (key) =>
          JSON.stringify(currentFilter[key]) !==
          JSON.stringify(prevFilter[key]),
      );
      const currKey = changedKey || keys.find((key) => currentFilter[key]);
      const prevKey = changedKey || keys.find((key) => prevFilter[key]);

      syncFilterState(
        currKey ? currentFilter[currKey] : undefined,
        prevKey ? prevFilter[prevKey] : undefined,
        setFilterState,
        currKey ? currentFilter[currKey]?.filterOptions : undefined,
        userActionSourceRef.current === sourceType,
      );
    };

    processCategoryOrStatus(
      categoryKeys,
      setCategoryFilterSelected,
      "category",
    );
    processCategoryOrStatus(statusKeys, setStatusFilterSelected, "status");

    const statusFilterKeys = statusKeys.filter((key) => currentFilter[key]);
    if (statusFilterKeys.length > 0 && statusFilterSelected.length === 0) {
      initFilterSelected(
        statusFilterKeys,
        currentFilter,
        setStatusFilterSelected,
      );
    }

    const categoryFilterKeys = categoryKeys.filter((key) => currentFilter[key]);
    if (categoryFilterKeys.length > 0 && categoryFilterSelected.length === 0) {
      initFilterSelected(
        categoryFilterKeys,
        currentFilter,
        setCategoryFilterSelected,
      );
    }

    const timer = setTimeout(() => {
      userActionSourceRef.current = null;
    }, 0);
    previousFilterInfoRef.current = currentFilter;

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterInfo,
    setCategoryFilterSelected,
    setStatusFilterSelected,
    categoryKeys,
    statusKeys,
  ]);

  // Sync from statusFilterSelected/categoryFilterSelected to filterInfo
  useEffect(() => {
    const prevStatusFilterSelected = previousStatusFilterSelectedRef.current;
    const prevCategoryFilterSelected =
      previousCategoryFilterSelectedRef.current;

    const isStatusChanged =
      JSON.stringify(statusFilterSelected) !==
      JSON.stringify(prevStatusFilterSelected);
    const isCategoryChanged =
      JSON.stringify(categoryFilterSelected) !==
      JSON.stringify(prevCategoryFilterSelected);

    const currentFilter = filterInfoRef.current.filter || {};
    const newFilter = { ...currentFilter };

    const isStatusFilterActive = statusFilterSelected.some(
      (x: any) => x.status === "UNCHECKED",
    );
    const isStatusMissingInFilterInfo = statusKeys.every(
      (key) => !currentFilter[key],
    );
    const shouldSyncStatus =
      isStatusChanged || (isStatusFilterActive && isStatusMissingInFilterInfo);

    const isCategoryFilterActive = categoryFilterSelected.some(
      (x: any) => x.status === "UNCHECKED",
    );
    const isCategoryMissingInFilterInfo = categoryKeys.every(
      (key) => !currentFilter[key],
    );
    const shouldSyncCategory =
      isCategoryChanged ||
      (isCategoryFilterActive && isCategoryMissingInFilterInfo);

    if (!shouldSyncStatus && !shouldSyncCategory) return;

    let hasActualChange = false;

    const processFilterChanges = (
      shouldSync: boolean,
      keys: string[],
      filterSelected: FilterSelected[],
      sourceType: "status" | "category",
    ) => {
      if (!shouldSync || keys.length === 0) return;

      hasActualChange = true;

      const visibleItems = filterSelected.filter((x: any) => !x.is_hidden);
      const checkedVisibleItems = visibleItems.filter(
        (x: any) => x.status === "CHECKED" || x.status === "SELECTED",
      );

      const isCheckAll =
        sourceType === "status"
          ? visibleItems.length > 0 &&
            checkedVisibleItems.length === visibleItems.length
          : visibleItems.length === 0 ||
            checkedVisibleItems.length === visibleItems.length;

      const allCheckedValues = filterSelected
        .filter((x: any) => x.status === "CHECKED" || x.status === "SELECTED")
        .map((x) => x.value);
      const isUncheckAll =
        filterSelected.length > 0 && allCheckedValues.length === 0;

      const managedValues = filterSelected.map((x) => String(x.value));

      let activeKeys = keys;
      if (gridApi) {
        const columnDefs = gridApi.getColumnDefs();
        if (columnDefs && columnDefs.length > 0) {
          const gridColIds = columnDefs
            .map((c: any) => c.colId || c.field)
            .filter(Boolean);
          const presentKeys = keys.filter((k) => gridColIds.includes(k));
          if (presentKeys.length > 0) {
            activeKeys = presentKeys;
          }
        }
      }

      activeKeys.forEach((key) => {
        const oldFilter = currentFilter[key]?.filter;

        let oldFilterKeys: string[] = [];
        if (Array.isArray(oldFilter)) {
          oldFilterKeys = oldFilter.map(String);
        } else if (typeof oldFilter === "object" && oldFilter !== null) {
          oldFilterKeys = Object.keys(oldFilter)
            .filter((k) => oldFilter[k] === true)
            .map(String);
        }

        let newFilterArr: any = undefined;
        if (!isCheckAll) {
          if (isUncheckAll) {
            newFilterArr = [];
          } else {
            const unmanagedValues = oldFilterKeys.filter(
              (val: any) => !managedValues.includes(String(val)),
            );
            newFilterArr = [...allCheckedValues, ...unmanagedValues];
          }
        }

        let newFilterSpecialEmpty = currentFilter[key]?.filter_special_empty;
        let shouldDeleteSpecialEmpty = false;
        if (isCheckAll) {
          shouldDeleteSpecialEmpty = true;
        } else if (managedValues.includes(SPECIAL_NULL)) {
          newFilterSpecialEmpty = allCheckedValues.includes(SPECIAL_NULL);
        } else {
          newFilterSpecialEmpty = newFilterSpecialEmpty ?? false;
        }

        if (newFilterArr) {
          newFilterArr = newFilterArr.filter((v: any) => v !== SPECIAL_NULL);
        }

        const actuallyNeedsDeleteSpecialEmpty =
          shouldDeleteSpecialEmpty &&
          currentFilter[key] &&
          (currentFilter[key].filter_special_empty !== undefined ||
            currentFilter[key].filter !== undefined);

        const isFilterArrayChanged = isCheckAll
          ? currentFilter[key]?.filter !== undefined &&
            !currentFilter[key]?.isCheckAll
          : JSON.stringify(newFilterArr) !== JSON.stringify(oldFilterKeys);

        if (
          isFilterArrayChanged ||
          currentFilter[key]?.filter_special_empty !== newFilterSpecialEmpty ||
          actuallyNeedsDeleteSpecialEmpty
        ) {
          hasActualChange = true;
          const updatedColFilter = {
            ...(newFilter[key] || {}),
            filter: newFilterArr
              ? newFilterArr.reduce((acc: any, val: any) => {
                  acc[val] = true;
                  return acc;
                }, {})
              : undefined,
            filter_special_empty: newFilterSpecialEmpty,
            isCheckAll: isCheckAll ? true : undefined,
          };
          if (shouldDeleteSpecialEmpty) {
            delete updatedColFilter.filter_special_empty;
          }
          if (!isCheckAll) {
            delete updatedColFilter.isCheckAll;
          } else {
            delete updatedColFilter.filter;
          }
          if (updatedColFilter.filter === undefined) {
            delete updatedColFilter.filter;
          }
          newFilter[key] = updatedColFilter;
          userActionSourceRef.current = sourceType;
        }
      });
    };

    processFilterChanges(
      shouldSyncStatus,
      statusKeys,
      statusFilterSelected,
      "status",
    );
    processFilterChanges(
      shouldSyncCategory,
      categoryKeys,
      categoryFilterSelected,
      "category",
    );

    if (hasActualChange) {
      setFilterInfo({ ...filterInfoRef.current, filter: newFilter });
      shouldRefreshHeaderRef.current = true;
    }

    previousStatusFilterSelectedRef.current = statusFilterSelected;
    previousCategoryFilterSelectedRef.current = categoryFilterSelected;
  }, [
    statusFilterSelected,
    categoryFilterSelected,
    categoryKeys,
    statusKeys,
    setFilterInfo,
  ]);

  useEffect(() => {
    if (gridApi && shouldRefreshHeaderRef.current) {
      gridApi.refreshHeader();
      shouldRefreshHeaderRef.current = false;
    }
  }, [filterInfo, gridApi]);
  // protect data statusl, category restore form session when first render
  const hasMountedRef = useRef(false);

  const prevSelectedTabsRef = useRef<string[]>([]);

  // Re-apply is_auto_select logic when selectedTabs changes
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (!selectedTabs?.length) return;
    if (
      JSON.stringify(selectedTabs) ===
      JSON.stringify(prevSelectedTabsRef.current)
    )
      return;
    prevSelectedTabsRef.current = selectedTabs;

    const currentFilter = filterInfo.filter || {};
    const statusKeyExists = statusKeys.some((key) => currentFilter[key]);
    const categoryKeyExists = categoryKeys.some((key) => currentFilter[key]);

    // If no status filter is set, reapply is_auto_select logic
    if (!statusKeyExists && statusFilterSelected.length > 0) {
      setStatusFilterSelected((prev) =>
        prev.map((item) => ({
          ...item,
          status: item.is_auto_select ? "SELECTED" : "UNCHECKED",
        })),
      );
    }

    // If no category filter is set, reapply is_auto_select logic
    if (!categoryKeyExists && categoryFilterSelected.length > 0) {
      setCategoryFilterSelected((prev) =>
        prev.map((item) => ({
          ...item,
          status: item.is_auto_select ? "SELECTED" : "UNCHECKED",
        })),
      );
    }
  }, [
    selectedTabs,
    filterInfo,
    statusKeys,
    categoryKeys,
    statusFilterSelected,
    categoryFilterSelected,
    setStatusFilterSelected,
    setCategoryFilterSelected,
  ]);

  const syncOpenFilterWithSearchBox = useCallback(
    (newFilterInfo: FilterInfo, currentFilterInfo: FilterInfo) => {
      let updatedFilterInfo = newFilterInfo;

      const isOpeningNewColumn =
        updatedFilterInfo.open &&
        (!currentFilterInfo.open ||
          updatedFilterInfo.columnId !== currentFilterInfo.columnId);

      if (
        isOpeningNewColumn &&
        updatedFilterInfo.columnId &&
        (statusKeys.includes(updatedFilterInfo.columnId as string) ||
          categoryKeys.includes(updatedFilterInfo.columnId as string))
      ) {
        const colId = updatedFilterInfo.columnId as string;
        const isStatusField = statusKeys.includes(colId);

        const hasExistingFilter =
          currentFilterInfo.filter?.[colId] &&
          (currentFilterInfo.filter[colId].filter !== undefined ||
            currentFilterInfo.filter[colId].isCheckAll);

        if (!hasExistingFilter) {
          const targetSelected = isStatusField
            ? statusFilterSelected
            : categoryFilterSelected;

          const visibleItems = targetSelected.filter((x: any) => !x.is_hidden);
          const checkedVisibleItems = visibleItems.filter(
            (x: any) => x.status === "CHECKED" || x.status === "SELECTED",
          );

          const isCheckAll = isStatusField
            ? visibleItems.length > 0 &&
              checkedVisibleItems.length === visibleItems.length
            : visibleItems.length === 0 ||
              checkedVisibleItems.length === visibleItems.length;

          if (!isCheckAll) {
            const filterOptions =
              updatedFilterInfo.filter?.[colId]?.filterOptions;
            const filterSelectedValues = targetSelected
              .filter((x) => x.status === "CHECKED" || x.status === "SELECTED")
              .map((x) => {
                if (filterOptions) {
                  const matchedOption = filterOptions.find(
                    (o: any) =>
                      String(o.value) === String(x.value) ||
                      String(o.label) === String(x.value) ||
                      String(o.label) === String(x.label) ||
                      String(o.value) === String(x.label),
                  );
                  if (matchedOption) return matchedOption.value;
                }
                return x.value;
              });

            const oldFilter = currentFilterInfo.filter?.[colId]?.filter;
            const managedValues = targetSelected.map((x) => String(x.value));

            let oldFilterKeys: string[] = [];
            if (Array.isArray(oldFilter)) {
              oldFilterKeys = oldFilter.map(String);
            } else if (typeof oldFilter === "object" && oldFilter !== null) {
              oldFilterKeys = Object.keys(oldFilter)
                .filter((k) => oldFilter[k] === true)
                .map(String);
            }

            const unmanagedValues =
              oldFilterKeys.length > 0
                ? oldFilterKeys.filter(
                    (val: any) => !managedValues.includes(String(val)),
                  )
                : [];

            let newFilterValues =
              filterSelectedValues.length > 0 || unmanagedValues.length > 0
                ? [...filterSelectedValues, ...unmanagedValues]
                : [];

            const filterSpecialEmpty = newFilterValues.includes(SPECIAL_NULL);
            newFilterValues = newFilterValues.filter((v) => v !== SPECIAL_NULL);

            updatedFilterInfo = {
              ...updatedFilterInfo,
              filter: {
                ...(updatedFilterInfo.filter || {}),
                [colId]: {
                  ...(updatedFilterInfo.filter?.[colId] || {}),
                  filter: newFilterValues.reduce((acc: any, val: any) => {
                    acc[val] = true;
                    return acc;
                  }, {}),
                  filter_special_empty: filterSpecialEmpty,
                },
              },
            };
          } else {
            const newColFilter = {
              ...(updatedFilterInfo.filter?.[colId] || {}),
              isCheckAll: true,
            };
            delete newColFilter.filter;
            delete newColFilter.filter_special_empty;

            updatedFilterInfo = {
              ...updatedFilterInfo,
              filter: {
                ...(updatedFilterInfo.filter || {}),
                [colId]: newColFilter,
              },
            };
          }
        }
      }

      if (isOpeningNewColumn) {
        updatedFilterInfo = {
          ...currentFilterInfo,
          ...updatedFilterInfo,
          filter: {
            ...(currentFilterInfo.filter || {}),
            ...(updatedFilterInfo.filter || {}),
          },
        };
      }

      return updatedFilterInfo;
    },
    [statusKeys, categoryKeys, statusFilterSelected, categoryFilterSelected],
  );

  return {
    statusFilterSelected,
    setStatusFilterSelected,
    categoryFilterSelected,
    setCategoryFilterSelected,
    syncOpenFilterWithSearchBox,
  };
};
